import { useCallback, useEffect, useRef, useState } from "react"
import { SelectDropdown, type SelectDropdownSize } from "@/components/ui/select-dropdown"
import { Button } from "@/components/ui/primitives"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { useTranslation } from "@/hooks/use-translation"
import { fetchAssetTaskCandidates } from "@/lib/tasks-api"
import { cn } from "@/lib/utils"
import type { TaskAssetCandidate, TaskAssetKind } from "@/types/tasks"

const NEW_TASK = "new"

type PickState = {
  kind: TaskAssetKind
  tasks: TaskAssetCandidate[]
  resolve: (value: number | null | undefined) => void
}

/** null, если id нет среди кандидатов: в запрос уйдёт новая задача, а не чужая карточка. */
export function selectedAssetTaskId(tasks: TaskAssetCandidate[], taskId: number | null): number | null {
  if (taskId == null) return null
  return tasks.some((task) => task.id === taskId) ? taskId : null
}

export function AssetTaskSelect({
  tasks,
  value,
  onChange,
  className,
  size = "md",
}: {
  tasks: TaskAssetCandidate[]
  value: number | null
  onChange: (taskId: number | null) => void
  className?: string
  size?: SelectDropdownSize
}) {
  const { t } = useTranslation("tasks")
  const selected = selectedAssetTaskId(tasks, value) == null ? NEW_TASK : String(value)
  if (tasks.length === 0) return null

  const options = [
    { value: NEW_TASK, label: t("assetPick.new"), separatorAfter: true },
    ...tasks.map((task) => ({ value: String(task.id), label: `${task.key} ${task.title}` })),
  ]

  return (
    <label className={cn("block text-xs text-ui-text-secondary", className)}>
      {t("assetPick.label")}
      <SelectDropdown
        className="mt-1"
        value={selected}
        onChange={(next) => onChange(next === NEW_TASK ? null : Number(next))}
        options={options}
        size={size}
        ariaLabel={t("assetPick.label")}
      />
    </label>
  )
}

type CandidatesPhase = "idle" | "loading" | "ready" | "error"

// Пока форма уже открыта: поле появляется, когда кандидаты есть. Ошибка — строка для toast.
export function useAssetTaskCandidates(projectId: number, kind: TaskAssetKind, enabled: boolean) {
  const [tasks, setTasks] = useState<TaskAssetCandidate[]>([])
  const [phase, setPhase] = useState<CandidatesPhase>("idle")
  const [error, setError] = useState<string | null>(null)
  const [loadedKey, setLoadedKey] = useState<string | null>(null)
  const requestKey = `${projectId}:${kind}`

  useEffect(() => {
    if (!enabled || projectId <= 0) {
      setTasks([])
      setPhase("idle")
      setError(null)
      setLoadedKey(null)
      return
    }
    const controller = new AbortController()
    let cancelled = false
    setPhase("loading")
    setError(null)
    setTasks([])
    setLoadedKey(null)
    fetchAssetTaskCandidates(projectId, kind, controller.signal)
      .then((rows) => {
        if (cancelled) return
        setTasks(rows)
        setLoadedKey(requestKey)
        setPhase("ready")
      })
      .catch((fetchError: unknown) => {
        if (cancelled) return
        if (fetchError instanceof Error && fetchError.name === "AbortError") return
        setTasks([])
        setLoadedKey(null)
        setError(fetchError instanceof Error ? fetchError.message : String(fetchError))
        setPhase("error")
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [enabled, projectId, kind, requestKey])

  const ready = enabled && projectId > 0 && phase === "ready" && loadedKey === requestKey
  return {
    tasks: ready ? tasks : [],
    loading: enabled && projectId > 0 && phase !== "error" && !ready,
    error,
    ready,
  }
}

// undefined — отмена, null — новая задача, число — id существующей.
export function useAssetTaskPick() {
  const [state, setState] = useState<PickState | null>(null)
  const settled = useRef(false)

  const choose = useCallback(async (projectId: number, kind: TaskAssetKind) => {
    const tasks = await fetchAssetTaskCandidates(projectId, kind)
    if (tasks.length === 0) return null
    settled.current = false
    return new Promise<number | null | undefined>((resolve) => {
      setState({ kind, tasks, resolve })
    })
  }, [])

  const finish = (value: number | null | undefined) => {
    if (settled.current) return
    settled.current = true
    state?.resolve(value)
    setState(null)
  }

  const dialog = (
    <AssetTaskPickDialog
      open={state != null}
      kind={state?.kind ?? "character"}
      tasks={state?.tasks ?? []}
      onCancel={() => finish(undefined)}
      onConfirm={(taskId) => finish(taskId)}
    />
  )

  return { choose, dialog }
}

export function AssetTaskPickDialog({
  open,
  kind,
  tasks,
  onCancel,
  onConfirm,
}: {
  open: boolean
  kind: TaskAssetKind
  tasks: TaskAssetCandidate[]
  onCancel: () => void
  onConfirm: (taskId: number | null) => void
}) {
  const { t } = useTranslation(["tasks", "common"])
  const [taskId, setTaskId] = useState<number | null>(null)

  useEffect(() => {
    if (open) setTaskId(null)
  }, [open, kind])

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel()
      }}
      title={t(`assetPick.${kind}`)}
      closeAriaLabel={t("close", { ns: "common" })}
      size="sm"
      nested={3}
      bodyClassName="space-y-3 p-5 pt-4"
      footer={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
            {t("cancel", { ns: "common" })}
          </Button>
          <Button type="button" size="sm" onClick={() => onConfirm(selectedAssetTaskId(tasks, taskId))}>
            {t("assetPick.continue")}
          </Button>
        </>
      }
    >
      <AssetTaskSelect tasks={tasks} value={taskId} onChange={setTaskId} />
    </WorkspaceDialog>
  )
}
